'use client';

import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '../Calendar/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover';
import { formatDate, formatDayMonth as dayAndMonth } from '@/lib/format';
import { cn } from '@/util/utils';

/*
  The design's date RANGE: a bordered control with a calendar icon that opens two months beneath
  it (2497:22154), for a filter's From / To. A single day is `DateField`, which a person can type
  into as well as pick from (ALS-227); a range is picked, since typing two dates into one box is
  not a thing anybody does.

  It exists instead of two `<input type="date">`, which render the browser's own picker in the
  browser's own date format. That changes shape per browser and per locale, looks nothing like the
  frames, and is what Franka raised on 29 Aug 2026.
*/

export interface DatePickerProps {
    /** What the trigger says before anything is picked. */
    label?: string;
    id?: string;
    /** Overrides the default width, for a field that has to fill a drawer's grid cell. */
    className?: string;
    disabled?: boolean;
    selected?: DateRange;
    onSelect?: (range: DateRange | undefined) => void;
    /** Two, as the frame draws it. */
    numberOfMonths?: number;
    /** The last day that can be picked; every day after it is disabled in the calendar. */
    disabledAfter?: Date;
}

/**
 * What the trigger reads once something is picked; an open-ended range says which end it has.
 *
 * Through the app's own `formatDate`, "05 Sep 2026", and not date-fns's `format`, whose default
 * locale is en-US: that prints "Sep 5, 2026" whoever is reading, so the control would disagree
 * with the chip beside it and with every other date in this admin. The client is in London.
 *
 * A range within one year names that year once, "05 Sep to 20 Sep 2026", which is how the
 * prototype's own date ranges read. Both in full is four characters too many for the width this
 * control gets in a filter drawer's grid, and clipped the year off the end.
 */
function triggerText(selected: DateRange | undefined): string | undefined {
    const day = (date: Date) => formatDate(date.toISOString());
    const { from, to } = selected ?? {};
    if (from && to) {
        return from.getFullYear() === to.getFullYear()
            ? `${dayAndMonth(from)} to ${dayAndMonth(to)} ${to.getFullYear()}`
            : `${day(from)} to ${day(to)}`;
    }
    if (from) return `from ${day(from)}`;
    if (to) return `until ${day(to)}`;
    return undefined;
}

export function DatePicker({
    label = 'Pick a date',
    id,
    className,
    disabled,
    selected,
    onSelect,
    numberOfMonths = 2,
    disabledAfter,
}: DatePickerProps) {
    const picked = triggerText(selected);
    return (
        <Popover data-testid="date-picker">
            <PopoverTrigger asChild>
                {/* Drawn as the input it stands for (ui-date: Geist 14 on the control box,
                    a 15px muted icon), not as an outline button in the label style. */}
                <button
                    id={id}
                    type="button"
                    disabled={disabled}
                    data-slot="date-picker-trigger"
                    data-empty={picked === undefined}
                    className={cn(
                        'bg-card border-input text-foreground flex h-9 w-[280px] min-w-0 items-center gap-2 rounded-md border px-3 text-left text-sm shadow-xs pointer-coarse:min-h-11 pointer-coarse:text-base',
                        'focus-visible:border-ring transition-[color,box-shadow,border-color] duration-(--duration-fast) outline-none focus-visible:shadow-[0_0_0_3px_var(--outline)]',
                        'disabled:bg-muted disabled:text-text-locked data-[empty=true]:text-muted-foreground disabled:cursor-not-allowed disabled:shadow-none',
                        className,
                    )}
                >
                    <CalendarIcon
                        aria-hidden
                        className="text-muted-foreground size-[15px] shrink-0"
                    />
                    <span className="min-w-0 flex-1 truncate">{picked ?? label}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto">
                <Calendar
                    mode="range"
                    numberOfMonths={numberOfMonths}
                    selected={selected}
                    onSelect={onSelect}
                    disabled={disabledAfter ? { after: disabledAfter } : undefined}
                />
            </PopoverContent>
        </Popover>
    );
}
